/* eslint-disable no-mixed-spaces-and-tabs */
import React, { createContext, useContext, useReducer, ReactNode } from "react";

// Define the structure of an offer
export interface OfferProps {
	id: number;
	from: string;
	to: string;
	expiryStartDate: string;
	expiryEndDate: string;
	transport: {
		type: string;
		mark: string;
		year: string;
		mileage: string;
		number: string;
		gps: boolean;
	};
	pricePerKm: string;
	priceWithVAT?: string; // Optional field
	note?: string;
	status: "enabled" | "archived" | "pending";
	company?: {
		name: string;
		logoUrl?: string;
	};
	viewCount?: number; // Optional field
	responseCount?: number; // Optional field
	createdDate?: string; // Optional field
}

// Define the global state structure
interface GlobalState {
	offers: {
		enabled: OfferProps[];
		archived: OfferProps[];
		pending: OfferProps[];
	};
}

type Action =
	| {
			type: "SET_OFFERS";
			payload: {
				status: "enabled" | "archived" | "pending";
				offers: OfferProps[];
			};
	  }
	| { type: "ADD_OFFER"; payload: OfferProps }
	| {
			type: "UPDATE_OFFER_STATUS";
			payload: { id: number; newStatus: "enabled" | "archived" | "pending" };
	  }
	| { type: "DELETE_OFFER"; payload: { id: number } };

// Initial global state
const initialState: GlobalState = {
	offers: {
		enabled: [
			{
				id: 1,
				from: "Туркменистан, Ашхабад",
				to: "Корея, Сеул",
				expiryStartDate: "2024-09-14",
				expiryEndDate: "2024-09-20",
				transport: {
					type: "Тягач",
					mark: "Volvo FH",
					year: "2015",
					mileage: "1 020 690 км",
					number: "5PPP064",
					gps: true,
				},
				company: {
					name: "Logistics Corp",
					logoUrl:
						"https://dowletli.com.tm/texapp/uploads/2024-11-25/Chat-c257a300-b06b-438c-a69a-1df2780468bc.png",
				},
				pricePerKm: "100 $",
				note: "Что стоит знать заказчику?",
				status: "enabled",
				viewCount: 0,
				responseCount: 0,
				createdDate: "2024-09-04",
			},
			{
				id: 2,
				from: "Казань",
				to: "Екатеринбург",
				expiryStartDate: "2024-11-01",
				expiryEndDate: "2024-11-30",
				transport: {
					type: "Фура",
					mark: "Scania R450",
					year: "2017",
					mileage: "800 000 км",
					number: "7CCC123",
					gps: false,
				},
				company: {
					name: "Logistics Corp",
					logoUrl:
						"https://dowletli.com.tm/texapp/uploads/2024-11-25/Chat-c257a300-b06b-438c-a69a-1df2780468bc.png",
				},
				pricePerKm: "7.50 ₽/км",
				note: "Ограничение по весу",
				status: "enabled",
				viewCount: 0,
				responseCount: 0,
				createdDate: "2024-10-10",
			},
		],
		archived: [],
		pending: [],
	},
};

// Reducer function to manage state updates
const globalStateReducer = (
	state: GlobalState,
	action: Action,
): GlobalState => {
	switch (action.type) {
		case "SET_OFFERS":
			return {
				...state,
				offers: {
					...state.offers,
					[action.payload.status]: action.payload.offers,
				},
			};
		case "ADD_OFFER":
			// Ensure the offer is always added to the `pending` status
			return {
				...state,
				offers: {
					...state.offers,
					pending: [
						...state.offers.pending,
						{ ...action.payload, status: "pending" }, // Force status to "pending"
					],
				},
			};
		case "UPDATE_OFFER_STATUS": {
			const { id, newStatus } = action.payload;
			const updatedOffers = { ...state.offers };

			// Find the offer and move it to the new status
			Object.keys(updatedOffers).forEach((status) => {
				const statusKey = status as "enabled" | "archived" | "pending";
				const index = updatedOffers[statusKey].findIndex(
					(offer) => offer.id === id,
				);
				if (index > -1) {
					const [movedOffer] = updatedOffers[statusKey].splice(index, 1);
					movedOffer.status = newStatus;
					updatedOffers[newStatus].push(movedOffer);
				}
			});

			return { ...state, offers: updatedOffers };
		}

		case "DELETE_OFFER":
			return {
				...state,
				offers: {
					enabled: state.offers.enabled.filter(
						(offer) => offer.id !== action.payload.id,
					),
					archived: state.offers.archived.filter(
						(offer) => offer.id !== action.payload.id,
					),
					pending: state.offers.pending.filter(
						(offer) => offer.id !== action.payload.id,
					),
				},
			};
		default:
			return state;
	}
};

// Create the context
const GlobalStateContext = createContext<{
	state: GlobalState;
	dispatch: React.Dispatch<Action>;
}>({ state: initialState, dispatch: () => undefined });

// Provider component to wrap the app
export const GlobalStateProvider = ({ children }: { children: ReactNode }) => {
	const [state, dispatch] = useReducer(globalStateReducer, initialState);

	return (
		<GlobalStateContext.Provider value={{ state, dispatch }}>
			{children}
		</GlobalStateContext.Provider>
	);
};

// Hook to use global state in components
export const useGlobalState = () => useContext(GlobalStateContext);
